import { useEffect, useRef } from 'react';
import { useAuth } from '@/services/auth';
import logger from '../utils/logger';

/**
 * Activity tracking request type
 */
export interface ActivityTrackingRequest {
  activityType: string;
  targetType: string;
  targetId: string;
  metadata?: Record<string, unknown>;
}

// Track if we've already warned about missing GraphQL migration
let hasWarnedAboutMigration = false;

/**
 * Track activities via GraphQL mutation
 * TODO: Migrate to GraphQL - REST API has been removed
 */
async function trackActivities(activities: ActivityTrackingRequest[]): Promise<{ tracked: boolean; activities: Array<{ activityGlobalId: string; targetId: string; occurrenceCount: number }> }> {
  // Only warn once per session to avoid console spam
  if (!hasWarnedAboutMigration) {
    logger.warn('Activity', 'Activity tracking not available - needs GraphQL migration');
    hasWarnedAboutMigration = true;
  }
  // Return empty success - activity tracking is non-critical
  return {
    tracked: false,
    activities: activities.map(a => ({ activityGlobalId: '', targetId: a.targetId, occurrenceCount: 0 })),
  };
}

interface UseActivityTrackingConfig {
  activityType: 'view';
  entityType: 'photo' | 'critique' | 'collection';
  entityId: string; // Global ID
  metadata?: {
    source?: string;
    duration?: number;
    viewportVisibility?: number;
    [key: string]: unknown;
  };
  threshold?: number; // Intersection Observer threshold (default: 0.5 = 50%)
  minDuration?: number; // Minimum visibility duration in ms (default: 1000 = 1 second)
}

// Global batch queue shared across all instances of the hook
let batchQueue: ActivityTrackingRequest[] = [];
let batchTimeout: NodeJS.Timeout | null = null;
const BATCH_WINDOW_MS = 2000; // 2 seconds

/**
 * Process the batch queue by sending all accumulated activities to the API
 */
async function processBatchQueue() {
  if (batchQueue.length === 0) return;

  const activitiesToSend = [...batchQueue];
  batchQueue = []; // Clear queue immediately
  batchTimeout = null;

  try {
    logger.debug('Activity', 'Processing batch queue', {
      count: activitiesToSend.length,
      activities: activitiesToSend.map(a => ({ type: a.activityType, target: a.targetType, id: a.targetId })),
    });

    const response = await trackActivities(activitiesToSend);

    logger.debug('Activity', 'Batch activities tracked successfully', {
      tracked: response.tracked,
      count: activitiesToSend.length,
      activities: response.activities.map(a => ({ id: a.activityGlobalId, target: a.targetId, count: a.occurrenceCount })),
    });
  } catch (error) {
    logger.error('Activity', 'Failed to track batch activities', {
      error,
      count: activitiesToSend.length,
      activities: activitiesToSend.map(a => ({ type: a.activityType, target: a.targetType, id: a.targetId })),
    });

    // Clear sessionStorage for failed items so they can be retried in next session
    activitiesToSend.forEach(activity => {
      const key = `activity:${activity.activityType}:${activity.targetType}:${activity.targetId}`;
      sessionStorage.removeItem(key);
      logger.warn('Activity', 'Cleared failed activity from session', { targetId: activity.targetId });
    });
  }
}

/**
 * Add activity to the batch queue and schedule processing
 */
function queueActivity(activity: ActivityTrackingRequest) {
  logger.debug('Activity', 'Queuing activity for batch processing', {
    type: activity.activityType,
    target: activity.targetType,
    id: activity.targetId,
    queueSize: batchQueue.length + 1,
  });

  batchQueue.push(activity);

  // Reset batch timeout
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  batchTimeout = setTimeout(() => {
    processBatchQueue();
  }, BATCH_WINDOW_MS);
}

/**
 * Check if activity has been tracked in current session using sessionStorage
 */
function isTrackedInSession(
  activityType: string,
  entityType: string,
  entityId: string
): boolean {
  const key = `activity:${activityType}:${entityType}:${entityId}`;
  return sessionStorage.getItem(key) !== null;
}

/**
 * Mark activity as tracked in current session
 */
function markTrackedInSession(
  activityType: string,
  entityType: string,
  entityId: string
) {
  const key = `activity:${activityType}:${entityType}:${entityId}`;
  sessionStorage.setItem(key, 'true');
}

/**
 * React hook for automatic activity tracking using Intersection Observer
 *
 * Tracks when an element becomes visible in the viewport and meets the minimum
 * visibility criteria (threshold % visible for minDuration ms).
 *
 * Features:
 * - Intersection Observer API for efficient visibility detection
 * - Session-based deduplication (won't track same view twice per session)
 * - 2-second batching window to reduce network requests
 * - Authenticated users only (no tracking for anonymous users)
 *
 * @param config Configuration for activity tracking
 * @returns Object with ref to attach to the element you want to track
 *
 * @example
 * ```tsx
 * function PhotoCard({ photo }) {
 *   const { ref } = useActivityTracking({
 *     activityType: 'view',
 *     entityType: 'photo',
 *     entityId: photo.globalId,
 *     metadata: { source: 'feed' }
 *   });
 *
 *   return <article ref={ref} className="photo-card">...</article>;
 * }
 * ```
 */
export function useActivityTracking(config: UseActivityTrackingConfig) {
  const {
    activityType,
    entityType,
    entityId,
    metadata,
    threshold = 0.5, // 50% visibility
    minDuration = 1000, // 1 second
  } = config;

  const { user } = useAuth();
  const elementRef = useRef<HTMLElement>(null);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    // Don't track for unauthenticated users
    if (!user) {
      logger.debug('Activity', 'Skipping tracking for unauthenticated user');
      return;
    }

    // Don't track if already tracked in this session
    if (isTrackedInSession(activityType, entityType, entityId)) {
      logger.debug('Activity', 'Skipping duplicate tracking in session', {
        activityType,
        entityType,
        entityId,
      });
      return;
    }

    const element = elementRef.current;
    if (!element) {
      logger.debug('Activity', 'Element ref not attached');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
            // Element is visible at threshold level - start timer
            if (!visibilityTimerRef.current && !hasTrackedRef.current) {
              logger.debug('Activity', 'Element became visible, starting timer', {
                entityType,
                entityId,
                intersectionRatio: entry.intersectionRatio,
                threshold,
                minDuration,
              });

              visibilityTimerRef.current = setTimeout(() => {
                // Element has been visible for minimum duration - track it
                logger.debug('Activity', 'Minimum duration met, tracking activity', {
                  activityType,
                  entityType,
                  entityId,
                  metadata,
                });

                queueActivity({
                  activityType,
                  targetType: entityType,
                  targetId: entityId,
                  metadata,
                });

                markTrackedInSession(activityType, entityType, entityId);
                hasTrackedRef.current = true;
              }, minDuration);
            }
          } else {
            // Element is no longer visible - cancel timer
            if (visibilityTimerRef.current) {
              logger.debug('Activity', 'Element no longer visible, cancelling timer', {
                entityType,
                entityId,
              });
              clearTimeout(visibilityTimerRef.current);
              visibilityTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold, // Trigger when element reaches threshold visibility
      }
    );

    observer.observe(element);

    // Cleanup
    return () => {
      observer.disconnect();
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };
  }, [user, activityType, entityType, entityId, metadata, threshold, minDuration]);

  return { ref: elementRef };
}
